// src/pages/nafed/pmc/PmcMouSigning.js

import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc, runTransaction, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../../firebaseConfig";
import { createMouPdf, sameMouRecord, urlToDataUrl, compactSignatureDataUrl, getMouDataSnapshot, isCurrentMouSignature, MOU_TEMPLATE_VERSION, canReacceptPmcRevision, getCurrentFinalMouUrl } from "../pacs/PacsMouSigning";

const MAX_SIGNATURE_SIZE = 2 * 1024 * 1024;
const clean = (value) => String(value ?? "").trim();
const upper = (value) => clean(value).toUpperCase();
const firstValue = (...values) => values.map(clean).find(Boolean) || "";
const safeName = (value = "file") =>
  String(value).replace(/[^a-zA-Z0-9._-]/g, "_").replace(/_+/g, "_").slice(0, 100);
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("PMC signature image could not be read."));
    reader.readAsDataURL(file);
  });

const sha256 = async (blob) => {
  if (!window.crypto?.subtle) return "";
  const buffer = await blob.arrayBuffer();
  const result = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(result))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};


const revisionProject = (project, signer) => ({...project,
  mouDocumentProfile: project.mouDocumentProfile || {},
  pmcSignatoryName: clean(signer.name), pmcSignatoryDesignation: clean(signer.designation)});

export async function commitPmcMouSignature({project, signer, signatureFile, accepted, reviewedSnapshot}) {
  const user = auth.currentUser;
  if (!user || !project.id) throw new Error("Application or login session is unavailable.");
  if (project.pacsOwnerAuthUid === user.uid) throw new Error("PACS login cannot sign for PMC.");
  if (!accepted) throw new Error("Read and accept the revised MoU before signing.");
  if (!canReacceptPmcRevision(project,user) && ![project.pmcId,project.allocatedPmcUid,project.allocatedPmcId].includes(user.uid)) {
    throw new Error("This Project is not linked to the current PMC login. Use its assigned PMC signatory account.");
  }
  if (!clean(signer.name) || !clean(signer.designation)) throw new Error("PMC signatory name and designation are required.");
  const proposed = revisionProject(project,signer);
  const expected = getMouDataSnapshot({},proposed);
  // Submit includes explicit acceptance; opening Preview is optional.
  const record = doc(db,"godown_projects",project.id);
  const liveDoc = await getDoc(record);
  if (!liveDoc.exists()) throw new Error("Project no longer exists.");
  const live = {...liveDoc.data(),id:project.id};
  if (getMouDataSnapshot({},revisionProject(live,signer)) !== expected ||
      !sameMouRecord(live.mouSignatures || {}, project.mouSignatures || {})) {
    throw new Error("Project or signatures changed. Refresh and preview again.");
  }
  const oldSignature = live.mouSignatures?.pmc || {};
  let signatureUrl = oldSignature.signatureUrl || live.pmcSignatureUrl || "";
  let signaturePath = oldSignature.signatureStoragePath || "";
  let image;
  if (signatureFile) {
    if (!["image/png","image/jpeg"].includes(signatureFile.type) || signatureFile.size > MAX_SIGNATURE_SIZE) throw new Error("Upload a PNG/JPG signature up to 2 MB.");
    image = await compactSignatureDataUrl(await fileToDataUrl(signatureFile));
    signaturePath = `nafed_pmc/${user.uid}/${live.id}/mou/signatures/pmc_${Date.now()}_${safeName(signatureFile.name)}`;
    const imageRef = ref(storage,signaturePath);
    await uploadBytes(imageRef,signatureFile,{contentType:signatureFile.type}).catch(error => { throw new Error("PMC signature upload failed. Check Firebase connectivity/access and retry. " + (error.code || error.message)); });
    signatureUrl = await getDownloadURL(imageRef);
  } else {
    if (!signatureUrl && !oldSignature.signatureDataUrl) throw new Error("Upload the PMC signature once; no saved image is available.");
    image = await urlToDataUrl(oldSignature.signatureDataUrl || signatureUrl);
  }
  if (!image) throw new Error("PMC signature image could not be loaded.");
  const signedAtIso = new Date().toISOString();
  const signature = {status:"SIGNED",party:"PMC",signingOrderPosition:1,name:clean(signer.name),designation:clean(signer.designation),signatureUrl,signatureDataUrl:image,signatureStoragePath:signaturePath,
    signedAtIso,signedByUid:user.uid,signedByEmail:user.email || "",templateVersion:MOU_TEMPLATE_VERSION,dataSnapshot:expected,
    declarationAccepted:true,consentTextVersion:MOU_TEMPLATE_VERSION,consentText:"I have reviewed and accept this MoU revision and authorize use of the selected or saved PMC signature."};
  const nextPacsSignature = {...(live.mouSignatures?.pacs || {}),status:"PENDING_REACCEPTANCE"};
  const working = {...proposed,mouTemplateVersion:MOU_TEMPLATE_VERSION,mouGeneratedAtIso:signedAtIso,mouDraftDataSnapshot:expected,
    mouFullyExecutedAtIso:"",mouSignatures:{...live.mouSignatures,pmc:signature,pacs:nextPacsSignature}};
  // PMC signs first. Old PACS signature images must not be embedded in the revised draft.
  const output = createMouPdf({profile:{},project:working,pmcSignatureDataUrl:image,pmcSigner:signature});
  if (output.missingFields.length) throw new Error(`Complete Project records: ${output.missingFields.join(", ")}`);
  const pdfHash = await sha256(output.blob);
  const pdfPath = `nafed_pmc/${user.uid}/${live.id}/mou/pmc_signed/${Date.now()}_${output.fileName}`;
  const pdfRef = ref(storage,pdfPath);
  await uploadBytes(pdfRef,output.blob,{contentType:"application/pdf",customMetadata:{projectId:live.id,templateVersion:MOU_TEMPLATE_VERSION,stage:"PENDING_PACS_SIGNATURE",signedByUid:user.uid}}).catch(error => { throw new Error("Signed MoU PDF upload failed. Signature has not been committed. Check Firebase connectivity/access and retry. " + (error.code || error.message)); });
  const pdfUrl = await getDownloadURL(pdfRef);
  Object.assign(signature,{signedPdfUrl:pdfUrl,signedPdfStoragePath:pdfPath,signedPdfSha256:pdfHash});
  const patch = {mouNumber:output.mouNumber,mouAgreementType:"PMC_PACS_TWO_PARTY",mouSigningOrder:["PMC","PACS"],
    mouTemplateVersion:MOU_TEMPLATE_VERSION,mouDocumentProfile:working.mouDocumentProfile,mouGeneratedAtIso:signedAtIso,mouDraftDataSnapshot:expected,
    mouDraftUrl:pdfUrl,mouDraftStoragePath:pdfPath,mouDraftSha256:pdfHash,pmcSignedMouUrl:pdfUrl,
    pmcSignatureUrl:signatureUrl,pmcSignatoryName:signature.name,pmcSignatoryDesignation:signature.designation,
    pmcSignerName:signature.name,pmcSignerDesignation:signature.designation,
    mouStage:"PENDING_PACS_SIGNATURE",mouStatus:"PENDING_PACS_SIGNATURE",currentStage:"MOU Pending PACS Final Signature",
    twoPartyMouExecuted:false,mouFullyExecuted:false,threePartyMouExecuted:false,finalExecutedMouUrl:"",mouFullyExecutedAtIso:"",
    finalMouUrl:"",finalSignedMouUrl:"",mouFinalPdfUrl:"",mouFinalPdfStoragePath:"",mouFinalPdfSha256:"",mouFinalTemplateVersion:"",branchSignedMouUrl:"",
    mouRevisionPreviousExecution:{templateVersion:live.mouTemplateVersion || "",pmc:oldSignature,pacs:live.mouSignatures?.pacs || {},
      finalUrl:getCurrentFinalMouUrl(live) || live.finalExecutedMouUrl || live.finalMouUrl || live.finalSignedMouUrl || live.mouFinalPdfUrl || ""}};
  await runTransaction(db,async transaction => {
    const freshDoc = await transaction.get(record);
    if (!freshDoc.exists()) throw new Error("Project no longer exists.");
    const fresh = {...freshDoc.data(),id:live.id};
    if (getMouDataSnapshot({},revisionProject(fresh,signer)) !== expected ||
        !sameMouRecord(fresh.mouSignatures || {}, live.mouSignatures || {}) ||
        JSON.stringify([fresh.pmcId,fresh.pmcUid,fresh.assignedPmcUid,fresh.pmcAuthUid,fresh.allocatedPmcUid,fresh.allocatedPmcId]) !== JSON.stringify([live.pmcId,live.pmcUid,live.assignedPmcUid,live.pmcAuthUid,live.allocatedPmcUid,live.allocatedPmcId])) {
      throw new Error("MoU details, allocation or a submitted signature changed while saving. Reload the project once and submit again.");
    }
    transaction.update(record,{...patch,"mouSignatures.pmc":signature,"mouSignatures.pacs":nextPacsSignature,updatedAt:serverTimestamp()});
  });
  // Auxiliary notification failure must not report a successfully committed signature as failed.
  for (const [collectionName, recordId, payload] of [
    ["nafed_notifications", `${live.id}_MOU_PACS_SIGNATURE`, {type:"MOU_PACS_SIGNATURE_REQUIRED",title:"PACS Final Signature Required on MOU",message:`PMC has accepted the current MoU for ${live.applicationId || live.id}. PACS may now sign.`,projectId:live.id,applicationId:live.applicationId || live.id,mouNumber:output.mouNumber,pmcUid:user.uid,state:live.state || "",branchUid:live.stateBranchUid || live.branchUid || "",visibleTo:["PACS","PMC","STATE_BRANCH","HEAD_OFFICE"],status:"ACTIVE",readBy:[],createdAt:serverTimestamp(),createdAtIso:signedAtIso}],
    ["nafed_activity", `${live.id}_PMC_MOU_SIGNED_${Date.now()}`, {action:"PMC_MOU_SIGNED_AND_FORWARDED_TO_PACS",entityType:"PMC_PACS_MOU",entityId:live.id,projectId:live.id,mouNumber:output.mouNumber,previousStage:live.mouStage || "",newStage:"PENDING_PACS_SIGNATURE",performedByUid:user.uid,performedByEmail:user.email || "",performedByRole:"PMC",pdfSha256:pdfHash,createdAt:serverTimestamp(),createdAtIso:signedAtIso}]
  ]) {
    try { await setDoc(doc(db,collectionName,recordId),payload,{merge:true}); }
    catch(error) { console.warn("PMC signature committed; optional notification/audit write failed",error); }
  }
  return {...patch,mouSignatures:{...live.mouSignatures,pmc:signature,pacs:nextPacsSignature}};
}

export default function PmcMouSigning({ profile = {}, project: suppliedProject = {}, onSaved }) {
  const [localProject,setLocalProject] = useState(null);
  useEffect(() => {setLocalProject(null);},[suppliedProject]);
  const project = localProject?.id === suppliedProject.id ? {...suppliedProject,...localProject} : suppliedProject;
  const [reviewedSnapshot,setReviewedSnapshot] = useState("");
  const signatures = project.mouSignatures || {};
  const pacsSignature = signatures.pacs || {};
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState("");
  const [signerName, setSignerName] = useState(
    project.pmcSignatoryName || signatures.pmc?.name || profile.name || profile.displayName || project.pmcSignerName || ""
  );
  const [designation, setDesignation] = useState(
    project.pmcSignatoryDesignation || signatures.pmc?.designation || profile.designation || "Authorised PMC Representative"
  );
  const [editingSignature, setEditingSignature] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const unchangedDraft = project.mouDraftDataSnapshot === getMouDataSnapshot({},project);
  const pacsSigned = unchangedDraft && isCurrentMouSignature(project,pacsSignature);
  const pmcSigned = unchangedDraft && isCurrentMouSignature(project,signatures.pmc);
  const previousPmcSigned = upper(signatures.pmc?.status) === "SIGNED";
  const savedSignatureUrl = signatures.pmc?.signatureDataUrl || signatures.pmc?.signatureUrl || project.pmcSignatureUrl || "";
  const documentApproved = previousPmcSigned || [project.documentStage,project.pmcDecision,project.pmcVerificationStatus,project.pmcCompleteDocumentSetStatus].some(value => ["PMC_DOCUMENTS_APPROVED","APPROVED","APPROVED_TO_WORKFLOW"].includes(upper(value)));
  const eligible = documentApproved;
  const currentFinalUrl = getCurrentFinalMouUrl(project);
  
  const mouNumber = useMemo(
    () => project.mouNumber || `${project.applicationId || project.id}/MOU/V${Number(project.mouVersion || 1)}`,
    [project]
  );

  const showMessage = (text, type = "info") => setMessage({ text, type });

  const previewCurrent = async () => {
    try {
      const candidate = revisionProject(project,{name:signerName,designation});
      const output = createMouPdf({profile:{},project:candidate,
        pmcSignatureDataUrl:!signatureFile && pmcSigned && savedSignatureUrl ? await urlToDataUrl(savedSignatureUrl) : "",
        pmcSigner:pmcSigned && !editingSignature ? signatures.pmc : null});
      window.open(output.pdf.output("bloburl"),"_blank","noopener,noreferrer");
      setReviewedSnapshot(getMouDataSnapshot({},candidate));
    } catch(error) {showMessage(error.message || "Preview failed.","error");}
  };
  const signAndForward = async () => {
    if (!eligible) return showMessage(pmcSigned ? "PMC has already signed the current revision." : "Approve the complete document set first.","error");
    try {
      setBusy("sign");
      const patch = await commitPmcMouSignature({project,signer:{name:signerName,designation},signatureFile,accepted,reviewedSnapshot});
      setLocalProject({...project,...patch}); onSaved?.(patch);
      setSignatureFile(null);setSignaturePreview("");setAccepted(false);setReviewedSnapshot("");setEditingSignature(false);
      showMessage("PMC signature saved. PACS can refresh and sign now.","success");
    } catch(error) {showMessage(error.message || "PMC signing failed.","error");}
    finally {setBusy("");}
  };

  const downloadCurrentFinal = async () => {
    const tab = window.open("about:blank","_blank");
    if (tab) tab.opener = null;
    try {
      const record = await getDoc(doc(db,"godown_projects",project.id));
      if (!record.exists()) throw new Error("Project not found.");
      const latest = {...record.data(),id:project.id};
      setLocalProject(latest); onSaved?.(latest);
      const url = getCurrentFinalMouUrl(latest);
      if (!url) throw new Error("Current final MoU is not ready. PMC must accept this revision and PACS must finalize it. Previous signed copies remain in history.");
      if (tab) tab.location.href = url; else window.location.assign(url);
    } catch(error) { if (tab) tab.close(); showMessage(error.message,"error"); }
  };

  return (
    <section className="pmc-mou">
      <style>{STYLE}</style>
      <header>
        <div>
          <small>MOU EXECUTION — PMC STAGE</small>
          <h2>PMC Review &amp; First Signature</h2>
          <p>
            PMC verifies the approved project particulars, signs first and
            forwards the two-party MoU to PACS for final execution.
          </p>
        </div>
        <strong>{pmcSigned && pacsSigned ? "FULLY_EXECUTED" : pmcSigned ? "PENDING_PACS_SIGNATURE" : "PENDING_PMC_SIGNATURE"}</strong>
      </header>
      {message.text && <div className={`message ${message.type}`}>{message.text}</div>}
      <div className="summary">
        <span>MOU Number<b>{mouNumber}</b></span>
        <span>PMC Signature<b>{pmcSigned ? "COMPLETED" : "PENDING"}</b></span>
        <span>PACS Final Signature<b>{pacsSigned ? "COMPLETED" : "AFTER PMC"}</b></span>
      </div>
      <div className="actions">
        <button onClick={previewCurrent}>Preview Latest MoU</button>
        <button onClick={downloadCurrentFinal} disabled={Boolean(busy)}>Download Current Final MoU</button>
      </div>
      <div className="message">Upload signature → accept declaration → Submit. Preview is optional.</div>
      {(
        <div className="form">
          <label>
            <span>PMC Signatory Name *</span>
            <input value={signerName} onChange={(e) => {setSignerName(e.target.value);setAccepted(false);}} />
          </label>
          <label>
            <span>Designation *</span>
            <input value={designation} onChange={(e) => {setDesignation(e.target.value);setAccepted(false);}} />
          </label>
          <label>
            <span>Signature PNG/JPG (optional when saved)</span>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={async (e) => {
                const file = e.target.files?.[0] || null;
                setAccepted(false); setReviewedSnapshot("");
                if (file && (!["image/png","image/jpeg"].includes(file.type) || file.size > MAX_SIGNATURE_SIZE)) {
                  e.target.value = ""; setSignatureFile(null); setSignaturePreview(""); showMessage("Upload a PNG/JPG signature up to 2 MB.","error"); return;
                }
                try { setSignatureFile(file); setSignaturePreview(file ? await fileToDataUrl(file) : ""); }
                catch(error) {setSignatureFile(null);setSignaturePreview("");showMessage(error.message,"error");}
              }}
            />
          </label>
          {(signaturePreview || savedSignatureUrl) && (
            <div className="signature-preview">
              <span>PMC Signature Preview</span>
              <img src={signaturePreview || savedSignatureUrl} alt="PMC signature preview" />
            </div>
          )}
          <label className="consent">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>
              I have verified the approved project and MOU particulars and am
              authorised to sign on behalf of the designated PMC. I accept this MoU and authorize applying the uploaded signature, or the displayed saved signature if no new file is selected.
            </span>
          </label>
          <button className="primary" onClick={signAndForward} disabled={Boolean(busy) || !accepted || !eligible}>
            {busy === "sign" ? "Submitting..." : "Submit PMC Signature"}
          </button>
        </div>
      )}
      {pmcSigned && !editingSignature && (
        <div className="done">
          PMC signature completed and MOU forwarded to PACS for final execution.
        </div>
      )}
    </section>
  );
}

const STYLE = `
.pmc-mou{padding:20px;border:1px solid #93A4B5;border-top:5px solid #123B69;border-radius:0;background:#F5F7F9;color:#1F2933;font-family:"Noto Sans",Arial,"Segoe UI",sans-serif}.pmc-mou *{box-sizing:border-box}.pmc-mou header{display:flex;justify-content:space-between;align-items:center;gap:20px;padding:16px 18px;border:1px solid #93A4B5;border-left:4px solid #123B69;background:#fff}.pmc-mou header small{color:#123B69;font-size:9px;font-weight:800;letter-spacing:.1em}.pmc-mou h2{margin:5px 0;font-family:Georgia,"Times New Roman",serif;font-size:21px}.pmc-mou p{margin:0;color:#617286;font-size:11px}.pmc-mou header strong{padding:8px 11px;border:1px solid #93A4B5;border-radius:0;background:#F1F4F7;font-size:9px}.message,.done{margin-top:14px;padding:11px 12px;border:1px solid #9FB5CC;border-radius:0;background:#F0F5FA}.message.error{border-color:#C98E8E;background:#FFF3F3;color:#8F2020}.message.success,.done{border-color:#93AFC9;background:#EEF4FA;color:#164F88}.summary{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;margin:15px 0;border:1px solid #93A4B5;background:#93A4B5}.summary span{padding:12px;background:#fff;color:#617286;font-size:9px}.summary b{display:block;margin-top:5px;color:#1F2933;font-size:11px}.actions{display:flex;gap:8px;margin-bottom:15px}.pmc-mou button,.pmc-mou a{padding:9px 11px;border:1px solid #123B69;border-radius:0;background:#fff;color:#123B69;font-size:10px;font-weight:800;text-decoration:none}.form{display:grid;grid-template-columns:1fr 1fr 1.2fr;gap:12px;padding:16px;border:1px solid #93A4B5;background:#fff}.form label span{display:block;margin-bottom:6px;font-size:9px;font-weight:800;text-transform:uppercase}.form input{width:100%;height:42px;padding:0 10px;border:1px solid #A8B5C2;border-radius:0}.form input[type=file]{padding:8px}.signature-preview{grid-column:1/-1;display:flex;align-items:center;gap:14px;padding:10px;border:1px dashed #93A4B5;background:#F8FAFC}.signature-preview span{font-size:9px;font-weight:800;text-transform:uppercase}.signature-preview img{width:190px;height:70px;object-fit:contain;background:#fff;border:1px solid #D5DEE7}.consent{display:flex!important;grid-column:1/-1;gap:9px;padding:11px;border:1px solid #BBC6D0;border-radius:0;background:#F4F6F8}.consent input{width:auto;height:auto}.consent span{margin:0!important;font-weight:500!important;text-transform:none!important}.primary{grid-column:1/-1;justify-self:end;border-color:#123B69!important;background:#123B69!important;color:#fff!important}.primary:disabled{opacity:.5}.done{font-weight:800}@media(max-width:760px){.summary,.form{grid-template-columns:1fr}.pmc-mou header{display:block}.pmc-mou header strong{display:inline-block;margin-top:12px}.primary{width:100%}.signature-preview{display:block}.signature-preview img{display:block;max-width:100%;margin-top:8px}}
`;
